import { Directive, OnInit, inject } from '@angular/core';
import { PoNotificationService, PoSelectOption, PoSkeletonVariant } from '@po-ui/ng-components';

import { RecursoRegra, RegraBase, RegrasApiService } from './regras-api.service';

/**
 * Comportamento comum às cinco abas de cadastro de regras: listagem paginada,
 * busca e filtro de situação no backend, carregar mais, inativar/reativar e o
 * esqueleto da carga inicial.
 *
 * Cada aba herda daqui e declara só o que a distingue — o recurso da API, as
 * colunas da tabela e o formulário. Sem isso seriam cinco cópias da mesma
 * paginação, e a primeira correção feita em uma só já as faria divergir.
 *
 * `@Directive()` sem seletor é o que permite a uma classe abstrata usar
 * lifecycle e injeção do Angular.
 */
@Directive()
export abstract class RegrasListaBase<T extends RegraBase> implements OnInit {
  protected readonly api = inject(RegrasApiService);
  protected readonly notif = inject(PoNotificationService);

  /** Segmento da rota na API (`portalnf/regras/<recurso>`). */
  protected abstract readonly recurso: RecursoRegra;

  /** Como o item aparece nas mensagens ao usuário: "regra", "de-para"... */
  protected abstract readonly rotulo: string;

  itens: T[] = [];
  loading = false;
  loadingMore = false;
  saving = false;

  page = 1;
  readonly pageSize = 20;
  hasMore = false;
  busca = '';
  filtroAtivo = '';

  readonly skeletonRect = PoSkeletonVariant.rectangle;
  readonly linhasSkeleton = [0, 1, 2, 3, 4, 5, 6, 7];

  readonly filtroSituacaoOptions: PoSelectOption[] = [
    { label: 'Todas', value: '' },
    { label: 'Somente ativas', value: '1' },
    { label: 'Somente inativas', value: '2' }
  ];

  readonly situacaoOptions: PoSelectOption[] = [
    { label: 'Ativo', value: '1' },
    { label: 'Inativo', value: '2' }
  ];

  readonly simNaoOptions: PoSelectOption[] = [
    { label: 'Sim', value: '1' },
    { label: 'Não', value: '2' }
  ];

  get tableHeight(): number {
    return Math.max(window.innerHeight - 380, 300);
  }

  ngOnInit(): void {
    this.aoIniciar();
    this.carregar();
  }

  /** Gancho para a aba carregar seus lookups. */
  protected aoIniciar(): void {
    // sem lookups por padrão
  }

  /** Filtros próprios da aba, anexados à query da listagem. */
  protected filtrosExtras(): Record<string, string> {
    return {};
  }

  /** Ajusta o item antes de exibir (colunas derivadas, formatação). */
  protected decorar(item: T): T {
    return item;
  }

  /** Corpo enviado ao backend. Cada aba monta o seu. */
  protected abstract corpo(item: T): Record<string, string>;

  carregar(): void {
    this.page = 1;
    this.itens = [];
    this.hasMore = false;
    this.loading = true;

    this.api.list<T>(this.recurso, this.page, this.pageSize, this.busca, this.filtroAtivo, this.filtrosExtras())
      .subscribe({
        next: res => {
          this.itens = res.data.map(i => this.decorar(i));
          this.hasMore = res.hasNext;
          this.loading = false;
        },
        error: err => {
          this.notif.error(err?.error?.message ?? `Erro ao carregar ${this.rotulo}.`);
          this.loading = false;
        }
      });
  }

  carregarMais(): void {
    if (!this.hasMore || this.loadingMore) { return; }
    this.loadingMore = true;
    this.page++;

    // Mantém busca e filtros: sem isso a próxima página viria da lista
    // completa, misturando registros fora do filtro em uso.
    this.api.list<T>(this.recurso, this.page, this.pageSize, this.busca, this.filtroAtivo, this.filtrosExtras())
      .subscribe({
        next: res => {
          this.itens = [...this.itens, ...res.data.map(i => this.decorar(i))];
          this.hasMore = res.hasNext;
          this.loadingMore = false;
        },
        error: () => {
          this.notif.error('Erro ao carregar mais registros.');
          this.loadingMore = false;
          this.page--;
        }
      });
  }

  filtrar(termo: string): void {
    this.busca = (termo || '').trim();
    this.carregar();
  }

  onFiltroSituacao(valor: string): void {
    this.filtroAtivo = valor ?? '';
    this.carregar();
  }

  /**
   * Inativa ou reativa. Não há exclusão em nenhum dos cadastros: regra usada em
   * nota entregue não pode sumir, sob pena de perder a rastreabilidade da
   * classificação aplicada (MIT044).
   */
  alterarSituacao(item: T, ativo: string): void {
    this.api.update(this.recurso, { ...this.corpo(item), ativo }).subscribe({
      next: () => {
        this.notif.success(ativo === '1' ? 'Registro reativado.' : 'Registro inativado.');
        this.carregar();
      },
      error: err => {
        this.notif.error(err?.error?.message ?? 'Erro ao alterar a situação.');
      }
    });
  }

  /** Grava a inclusão ou alteração e recarrega a listagem. */
  protected gravar(item: T, alterando: boolean, aoConcluir: () => void): void {
    this.saving = true;
    const body = this.corpo(item);
    const op$ = alterando ? this.api.update(this.recurso, body) : this.api.create(this.recurso, body);

    op$.subscribe({
      next: () => {
        this.saving = false;
        aoConcluir();
        this.notif.success(alterando ? 'Registro atualizado.' : 'Registro incluído.');
        this.carregar();
      },
      error: err => {
        this.saving = false;
        // A mensagem vem do modelo MVC (TES inexistente, conflito de ordem,
        // vigência sobreposta). Repassar o texto original é o que permite ao
        // usuário-chave corrigir sozinho.
        this.notif.error(err?.error?.message ?? 'Erro ao salvar.');
      }
    });
  }

  /**
   * Ordem com 3 dígitos ("10" -> "010"). Os campos `_ORDEM` são C(3) e a
   * comparação no banco é alfabética: gravar "10" faria a regra 10 ordenar
   * depois da 20 ("1" > "0"), invertendo o "menor ordem vence".
   */
  protected normalizarOrdem(valor: string | number): string {
    const limpo = String(valor ?? '').trim();
    if (!limpo || !/^\d{1,3}$/.test(limpo)) { return limpo; }
    return limpo.padStart(3, '0');
  }

  /** Data de hoje em ISO (AAAA-MM-DD), formato que a API espera. */
  protected hojeIso(): string {
    const d = new Date();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
  }

  protected formatarData(iso?: string): string {
    if (!iso || iso.length < 10) { return ''; }
    const [ano, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  /** Junta início e fim numa coluna só, como o protótipo da MIT apresenta. */
  protected vigenciaDe(dtIni?: string, dtFim?: string): string {
    const ini = this.formatarData(dtIni);
    const fim = this.formatarData(dtFim);
    if (ini && fim) { return `${ini} a ${fim}`; }
    if (ini) { return `desde ${ini}`; }
    if (fim) { return `até ${fim}`; }
    return '';
  }
}
